import { format, parse, subYears } from 'date-fns';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  COMPOSITION_GET_DATA_V2,
  PositionsEntityKind,
} from '@revelio/data-access';

import { useMonthApiFilters } from '../../entity-summary/use-month-api-filters';
import type { ResolvedProfileIds } from './useResolveProfileIds';

const TREND_LOOKBACK_YEARS = 3;

export interface RoleSkillTimepoint {
  id: number;
  date: string;
  count: number;
  share: number;
}

export interface RoleSkill {
  id: number;
  shortName: string;
  longName: string;
  latestShare: number;
  timeseries: RoleSkillTimepoint[];
}

export interface UseRoleSkillCompositionResult {
  roleSkills: RoleSkill[];
  isLoading: boolean;
  error: string | null;
}

export const useRoleSkillComposition = (
  resolvedIds: ResolvedProfileIds
): UseRoleSkillCompositionResult => {
  const dateFilters = useMonthApiFilters();

  const variables = useMemo(() => {
    if (!resolvedIds.roleId || !dateFilters) return undefined;

    const endDate = dateFilters.date_range_api_filters.end_date;
    const parsedEnd = parse(endDate, 'yyyy-MM', new Date());
    const startDate = format(
      subYears(parsedEnd, TREND_LOOKBACK_YEARS),
      'yyyy-MM'
    );

    return {
      entities: [
        {
          EntID: resolvedIds.roleId,
          EntType: PositionsEntityKind.RoleK150,
        },
      ],
      filters: {
        raw: false,
        start_date: startDate,
        end_date: endDate,
        ...(resolvedIds.seniorityId
          ? { seniority: [resolvedIds.seniorityId] }
          : {}),
      },
      breakdownOptions: {
        Skill: { all: true },
      },
    };
  }, [resolvedIds.roleId, resolvedIds.seniorityId, dateFilters]);

  const [{ data, fetching, error }] = useQuery({
    query: COMPOSITION_GET_DATA_V2,
    variables: variables ?? {
      entities: [],
      filters: { raw: false },
    },
    pause: !variables,
  });

  const roleSkills = useMemo((): RoleSkill[] => {
    const composition = data?.composition_v2;
    const entity = composition?.entities?.[0];
    if (!entity?.metrics?.skills) return [];

    const monthRefs: string[] = composition?.refs?.months ?? [];

    return entity.metrics.skills
      .filter(
        (skill): skill is NonNullable<typeof skill> =>
          skill !== null && skill.metadata !== null
      )
      .map((skill) => {
        const ts: RoleSkillTimepoint[] = (skill.timeseries ?? [])
          .filter((t): t is NonNullable<typeof t> => t !== null)
          .map((t) => ({
            id: t.id ?? 0,
            date: monthRefs[t.id ?? 0] ?? '',
            count: t.count ?? 0,
            share: t.share ?? 0,
          }));

        const latestShare = ts.length > 0 ? (ts[ts.length - 1]?.share ?? 0) : 0;

        return {
          id: skill.metadata?.id ? Number(skill.metadata.id) : 0,
          shortName: skill.metadata?.short_name ?? '',
          longName: skill.metadata?.long_name ?? '',
          latestShare,
          timeseries: ts,
        };
      })
      .sort((a, b) => b.latestShare - a.latestShare);
  }, [data]);

  return {
    roleSkills,
    isLoading: fetching,
    error: error ? 'Failed to load role skill composition' : null,
  };
};
